import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Upload, CheckCircle2, AlertCircle, FileText, X, ShieldCheck, RefreshCw, Camera } from 'lucide-react';
import api from '@/lib/api';
import { uploadFile } from '@/lib/uploads';
import { useAuth } from '@/lib/auth';
import MobileShell from '@/components/MobileShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { CITIES } from '@/lib/constants';
import { splitE164Phone } from '@/lib/phoneAuth';
import PhoneCountryInput, { validatePhoneCountryInput } from '@/components/auth/PhoneCountryInput';

export default function ProviderKYC() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [provider, setProvider] = useState(null);
  const [requirements, setRequirements] = useState([]);
  const [docs, setDocs] = useState([]);
  const [name, setName] = useState('');
  const [city, setCity] = useState('Bangalore');
  const [currentAddress, setCurrentAddress] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [countryCode, setCountryCode] = useState('IN');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingType, setUploadingType] = useState('');
  const [uploadProgress, setUploadProgress] = useState({});
  const autoSaveTimer = useRef(null);

  const fetchAll = async () => {
    const { data } = await api.get('/providers/me');
    setProvider(data.provider);
    setRequirements(data.provider?.kyc_required || []);
    setName(data.provider?.name || '');
    setCity(data.provider?.city || 'Bangalore');
    setCurrentAddress(data.provider?.current_address || '');
    setReferenceNumber(data.provider?.reference_number || '');
    setPhone(splitE164Phone(data.provider?.phone).local);
    setCountryCode(splitE164Phone(data.provider?.phone).countryCode);
    const docsRes = await api.get('/providers/me/documents');
    setDocs(docsRes.data.documents || []);
  };

  useEffect(() => { fetchAll().catch(() => {}); }, []);
  useEffect(() => () => clearTimeout(autoSaveTimer.current), []);

  const basicInfoComplete = Boolean(name.trim() && city && currentAddress.trim() && referenceNumber.trim() && phone.trim());
  const requiredDocsComplete = requirements.filter((req) => !/\(optional\)$/i.test(req)).every((req) => Boolean(docs.find((d) => d.document_type === req)));

  useEffect(() => {
    if (!basicInfoComplete) return undefined;
    clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      const phoneCheck = validatePhoneCountryInput(countryCode, phone);
      if (!phoneCheck.ok) return;
      try {
        setSaving(true);
        await api.patch('/providers/me', {
          name: name.trim(), city, current_address: currentAddress.trim(),
          reference_number: referenceNumber.trim().toUpperCase(), country_code: countryCode, phone: phoneCheck.digits,
        });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Could not save your details');
      } finally {
        setSaving(false);
      }
    }, 700);
    return () => clearTimeout(autoSaveTimer.current);
  }, [basicInfoComplete, name, city, currentAddress, referenceNumber, countryCode, phone]);


  const handleFile = async (docType, file) => {
    if (!file) return;
    setUploadingType(docType);
    setUploadProgress((prev) => ({ ...prev, [docType]: 0 }));
    try {
      const url = await uploadFile('provider-documents', file, {
        preserveFile: true,
        onProgress: (progress) => setUploadProgress((prev) => ({ ...prev, [docType]: progress })),
      });
      setUploadProgress((prev) => ({ ...prev, [docType]: 100 }));
      await api.post('/providers/me/documents', { document_type: docType, document_url: url, file_name: file.name });
      toast.success(docType + ' uploaded');
      await fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Upload failed'); }
    finally { setUploadingType(''); setUploadProgress((prev) => ({ ...prev, [docType]: 0 })); }
  };

  const removeDoc = async (id) => {
    try { await api.delete(`/providers/me/documents/${id}`); toast.success('Removed'); await fetchAll(); }
    catch (e) { toast.error('Could not remove'); }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post('/providers/me/submit');
      toast.success('Submitted for review');
      await refresh();
      navigate('/provider/status', { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed');
    } finally { setSubmitting(false); }
  };

  const docByType = (t) => docs.find((d) => d.document_type === t);
  const checks = provider?.verification?.checks || {};

  return (
    <MobileShell title="KYC & Documents" hideBack>
      <div className="px-5 py-5">
        <div className="resqly-card p-4 mb-4 bg-amber-50/50 border-amber-100">
          <p className="text-xs text-amber-800">Approval Status: <b className="capitalize">{provider?.approval_status || 'incomplete'}</b></p>
          {provider?.rejection_reason && (
            <p className="text-xs text-rose-700 mt-1">Reason: {provider.rejection_reason}</p>
          )}
        </div>

        <div className="resqly-card p-5 mb-4 space-y-4">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-blue-700" />
              <h3 className="font-semibold text-slate-900">Identity & Contact Details</h3>
            </div>
            <p className="text-xs text-slate-500 mt-1">Name, mobile number and reference number are verified first. This form uses your current address instead of a service-area field.</p>
          </div>
          <Field label="Full Name / Business Name" status={checks.name}>
            <Input data-testid="kyc-name" value={name} onChange={(e) => setName(e.target.value)} />
            <VerificationNote check={checks.name} label="Name" />
          </Field>
          <Field label="City">
            <select data-testid="kyc-city" value={city} onChange={(e) => setCity(e.target.value)} className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white">
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Current Address">
            <Textarea data-testid="kyc-current-address" value={currentAddress} onChange={(e) => setCurrentAddress(e.target.value)} rows={3} placeholder="House / building, street, locality, city, PIN code" />
          </Field>
          <Field label="Reference Number (Required for verification)" status={checks.reference}>
            <Input data-testid="kyc-reference-number" value={referenceNumber} onChange={(e) => setReferenceNumber(e.target.value.toUpperCase())} placeholder="Enter reference number" className="uppercase" />
            <VerificationNote check={checks.reference} label="Reference number" />
          </Field>
          <Field label="Phone Number" status={checks.phone}>
            <PhoneCountryInput
              countryCode={countryCode}
              onCountryCodeChange={setCountryCode}
              phone={phone}
              onPhoneChange={setPhone}
              countryTestId="kyc-country"
              phoneTestId="kyc-phone"
            />
            <VerificationNote check={checks.phone} label="Mobile number" />
          </Field>
          {saving && <div className="text-xs text-slate-400">Saving changes…</div>}
        </div>

        <div className="resqly-card p-5">
          <h3 className="font-semibold text-slate-900">Required Documents</h3>
          <p className="text-xs text-slate-500 mt-1">If admin asks for one file again, replace only that file. Other approved files stay approved.</p>
          <p className="text-xs text-slate-500 mt-1">Category: <b className="capitalize">{(provider?.category || '').replace('_', ' ')}</b></p>
          <div className="mt-4 space-y-3">
            {requirements.map((req) => {
              const doc = docByType(req);
              const progress = uploadProgress[req] || 0;
              const isSelfie = req === 'Selfie Verification';
              return (
                <div key={req} className="border border-slate-200 rounded-lg p-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2">
                      {doc?.verification_status === 'approved'
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
                        : (doc?.verification_status === 'resubmit' || doc?.verification_status === 'rejected')
                          ? <RefreshCw className="w-5 h-5 text-rose-600 mt-0.5" />
                          : <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />}
                      <div>
                        <div className="font-medium text-sm text-slate-900">{req}</div>
                        {doc && (
                          <>
                            <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-1"><FileText className="w-3 h-3" /> {doc.file_name || 'Uploaded'}
                              <span className={'ml-2 px-1.5 py-0.5 rounded-full text-[10px] font-semibold ' + (doc.verification_status === 'approved' ? 'bg-emerald-100 text-emerald-700' : (doc.verification_status === 'rejected' || doc.verification_status === 'resubmit') ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700')}>{doc.verification_status}</span>
                            </div>
                            {doc.admin_note && <p className="text-xs text-rose-700 mt-1">{doc.admin_note}</p>}
                          </>
                        )}
                      </div>
                    </div>
                    {doc && (
                      <button onClick={() => removeDoc(doc.id)} className="text-rose-500"><X className="w-4 h-4" /></button>
                    )}
                  </div>
                  <div className="mt-2">
                    <label className="inline-flex items-center gap-2 text-sm text-blue-700 cursor-pointer font-medium">
                      {isSelfie ? <Camera className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                      {isSelfie ? 'Open camera' : doc ? 'Replace' : 'Upload'}
                      <input
                        data-testid={`kyc-upload-${req.replace(/\s+/g, '-').toLowerCase()}`}
                        type="file"
                        accept={isSelfie ? 'image/*' : '*/*'}
                        capture={isSelfie ? 'user' : undefined}
                        onChange={(e) => handleFile(req, e.target.files?.[0])}
                        className="hidden"
                      />
                    </label>
                    {uploadingType === req && (
                      <div className="mt-2">
                        <div className="flex justify-between text-[11px] text-slate-500"><span>Uploading…</span><span>{progress}%</span></div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                          <div className="h-full bg-blue-600 transition-all" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Button data-testid="kyc-submit" onClick={submit} disabled={submitting || !basicInfoComplete || !requiredDocsComplete} className="w-full mt-5 bg-blue-700 hover:bg-blue-800">
          {submitting ? 'Submitting...' : 'Submit for Review'}
        </Button>
      </div>
    </MobileShell>
  );
}

function Field({ label, children, status }) {
  const tone = status?.status === 'verified'
    ? 'border-emerald-200 bg-emerald-50/30'
    : status?.status === 'resubmit'
      ? 'border-rose-200 bg-rose-50/30'
      : 'border-slate-200';
  return <div className={'rounded-lg border p-3 ' + tone}><label className="text-xs font-medium text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
}

function VerificationNote({ check, label }) {
  if (!check || check.status === 'verified' || !check.note) return null;
  return <p className="text-xs text-rose-700 mt-1">{label}: {check.note}</p>;
}
